import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api, unwrap, type ReviewItem } from "../api/client";
import { TIME_FROM, size, utc } from "../import/items";
import { goTo } from "../nav";

/** `YYYY-MM-DD HH:MM:SS[.mmm]` in UTC, or null if it isn't one. */
function parseUtc(text: string): string | null {
  const m = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2}(\.\d{1,3})?)Z?$/.exec(
    text.trim(),
  );
  if (!m) return null;
  const ms = Date.parse(`${m[1]}T${m[2]}Z`);
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
}

/**
 * Files the importer couldn't fully identify or time (design §6.5). Each is
 * given a source and its start time checked before it joins the timeline.
 */
export function NeedsReviewPage() {
  const queue = useQuery({
    queryKey: ["review"],
    queryFn: () => unwrap(api.GET("/api/review")),
  });
  const [open, setOpen] = useState<ReviewItem | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [bulk, setBulk] = useState(false);
  const [report, setReport] = useState<string | null>(null);
  const items = queue.data?.items ?? [];
  const selected = items.filter((i) => chosen.has(i.id));
  const unmatched = items.filter((i) =>
    i.problems.some((p) => p.includes("no filename profile")),
  ).length;
  const toggle = (id: string) =>
    setChosen((c) => {
      const next = new Set(c);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  return (
    <Stack spacing={2}>
      <Typography variant="h5" component="h2">
        Needs review
      </Typography>
      {queue.isError && <Alert severity="error">{queue.error.message}</Alert>}
      {queue.isSuccess && items.length === 0 && (
        <Alert severity="success">Nothing needs review.</Alert>
      )}
      {unmatched > 0 && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={() => goTo("/profiles")}>
              Filename profiles
            </Button>
          }
        >
          {unmatched} file{unmatched === 1 ? "" : "s"} match no filename
          profile. A profile made from their names identifies them, and later
          imports from the same device, without resolving each by hand; files
          waiting here are checked again when it is saved.
        </Alert>
      )}
      {report && (
        <Alert severity="info" onClose={() => setReport(null)}>
          {report}
        </Alert>
      )}
      {items.length > 0 && (
        <Stack direction="row" sx={{ gap: 2, alignItems: "center" }}>
          <Button
            variant="contained"
            disabled={selected.length === 0}
            onClick={() => setBulk(true)}
          >
            Accept {selected.length || ""} selected
          </Button>
          <Typography variant="body2" color="text.secondary">
            Accepting keeps each file's start time, and its source unless you
            give one for them all.
          </Typography>
        </Stack>
      )}
      {items.length > 0 && (
        <TableContainer>
          <Table
            size="small"
            sx={{ minWidth: 900, "& .nowrap": { whiteSpace: "nowrap" } }}
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    slotProps={{ input: { "aria-label": "Select all" } }}
                    checked={selected.length === items.length}
                    indeterminate={
                      selected.length > 0 && selected.length < items.length
                    }
                    onChange={(e) =>
                      setChosen(
                        new Set(e.target.checked ? items.map((i) => i.id) : []),
                      )
                    }
                  />
                </TableCell>
                <TableCell />
                <TableCell>File</TableCell>
                <TableCell>Team</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Start (UTC)</TableCell>
                <TableCell>Time from</TableCell>
                <TableCell align="right">Size</TableCell>
                <TableCell>Why</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((i) => (
                <TableRow key={i.id} selected={chosen.has(i.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      slotProps={{ input: { "aria-label": `Select ${i.original_name}` } }}
                      checked={chosen.has(i.id)}
                      onChange={() => toggle(i.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Button onClick={() => setOpen(i)}>Resolve</Button>
                  </TableCell>
                  <TableCell
                    className="nowrap"
                    sx={{ fontFamily: "monospace" }}
                  >
                    {i.unit ? `${i.unit}/` : ""}
                    {i.original_name}
                  </TableCell>
                  <TableCell>{i.team_name}</TableCell>
                  <TableCell>{i.source ?? "Unassigned"}</TableCell>
                  <TableCell
                    className="nowrap"
                    sx={{ fontFamily: "monospace" }}
                  >
                    {i.start_utc ? utc(i.start_utc) : "—"}
                  </TableCell>
                  <TableCell>
                    {i.time_basis
                      ? `${TIME_FROM[i.time_basis] ?? i.time_basis} (${i.time_confidence ?? "?"})`
                      : "—"}
                  </TableCell>
                  <TableCell className="nowrap" align="right">
                    {size(i.size_bytes)}
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      {i.problems.map((p) => (
                        <Chip
                          key={p}
                          size="small"
                          color="warning"
                          variant="outlined"
                          label={p}
                          sx={{ alignSelf: "flex-start" }}
                        />
                      ))}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {bulk && (
        <BulkDialog
          items={selected}
          onClose={(done) => {
            setBulk(false);
            if (done) {
              setReport(done);
              setChosen(new Set());
            }
          }}
        />
      )}
      {open && (
        <ResolveDialog
          item={open}
          sources={
            queue.data?.known_sources
              .filter((s) => s.library_id === open.library_id)
              .map((s) => s.source) ?? []
          }
          onClose={() => setOpen(null)}
        />
      )}
    </Stack>
  );
}

function ResolveDialog({
  item,
  sources,
  onClose,
}: {
  item: ReviewItem;
  sources: string[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [source, setSource] = useState(item.source ?? "");
  const [start, setStart] = useState(item.start_utc ? utc(item.start_utc) : "");
  const [reason, setReason] = useState("");
  const parsed = parseUtc(start);
  const moved =
    parsed !== null &&
    (!item.start_utc || Date.parse(parsed) !== Date.parse(item.start_utc));
  const end =
    parsed && item.start_utc && item.end_utc
      ? new Date(
          Date.parse(parsed) +
            (Date.parse(item.end_utc) - Date.parse(item.start_utc)),
        ).toISOString()
      : null;
  const resolve = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/review/{id}", {
          params: { path: { id: item.id } },
          body: {
            source: source.trim(),
            start_utc: moved ? parsed : undefined,
            reason: reason.trim(),
          },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["review"] });
      void qc.invalidateQueries({ queryKey: ["timeline"] });
      onClose();
    },
  });
  const ready = source.trim() !== "" && parsed !== null && reason.trim() !== "";

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Resolve {item.original_name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {item.problems.length > 0 && (
            <Alert severity="warning">{item.problems.join("; ")}</Alert>
          )}
          <Autocomplete
            freeSolo
            options={sources}
            inputValue={source}
            onInputChange={(_, v) => setSource(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Source"
                helperText="e.g. Van7/gps; sources already in this team are offered"
              />
            )}
          />
          <TextField
            label="Start (UTC)"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            error={start !== "" && parsed === null}
            helperText={
              start !== "" && parsed === null
                ? "YYYY-MM-DD HH:MM:SS, in UTC"
                : moved
                  ? `Changed: set by hand${end ? `; ends ${utc(end)}` : ""}`
                  : `Accepting the start from ${TIME_FROM[item.time_basis ?? ""] ?? "the file"}`
            }
            slotProps={{ htmlInput: { style: { fontFamily: "monospace" } } }}
          />
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            helperText="Recorded in the audit log"
            multiline
            required
          />
          {resolve.isError && (
            <Alert severity="error">{resolve.error.message}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!ready || resolve.isPending}
          onClick={() => resolve.mutate()}
        >
          Resolve
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BulkDialog({
  items,
  onClose,
}: {
  items: ReviewItem[];
  onClose: (report: string | null) => void;
}) {
  const qc = useQueryClient();
  const [source, setSource] = useState("");
  const [reason, setReason] = useState("");
  const unnamed = items.filter((i) => !i.source).length;
  const accept = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/review/bulk", {
          body: {
            ids: items.map((i) => i.id),
            source: source.trim() || undefined,
            reason: reason.trim(),
          },
        }),
      ),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["review"] });
      void qc.invalidateQueries({ queryKey: ["timeline"] });
      const names = new Map(items.map((i) => [i.id, i.original_name]));
      const failed = r.failed.map((f) => `${names.get(f.id) ?? f.id}: ${f.message}`);
      onClose(
        `${r.resolved.length} accepted.` +
          (failed.length ? ` Not accepted: ${failed.join("; ")}.` : ""),
      );
    },
  });
  const ready = reason.trim() !== "" && (unnamed === 0 || source.trim() !== "");
  return (
    <Dialog open onClose={() => onClose(null)} fullWidth maxWidth="sm">
      <DialogTitle>Accept {items.length} files</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Source for them all"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            helperText={
              unnamed > 0
                ? `${unnamed} have no source: give one, or leave them out`
                : "Leave empty to keep each file's own source"
            }
          />
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            helperText="Recorded in the audit log for each file"
            multiline
            required
          />
          {accept.isError && <Alert severity="error">{accept.error.message}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(null)}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!ready || accept.isPending}
          onClick={() => accept.mutate()}
        >
          Accept
        </Button>
      </DialogActions>
    </Dialog>
  );
}
